import React, { useState, useEffect, useCallback } from 'react';
import { api, API_BASE } from '../utils/api';
import { formatDateThai } from '../utils/helpers';
import { Loader2, AlertCircle, Printer, Filter, CalendarCheck, CalendarX } from 'lucide-react';

const getStatusBadge = (status) => {
  switch (status) {
    case 'pending': return 'bg-yellow-100 text-yellow-800';
    case 'approved': return 'bg-green-100 text-green-800';
    case 'rejected': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const getStatusText = (status) => {
  switch (status) {
    case 'pending': return 'รอพิจารณา';
    case 'approved': return 'อนุมัติแล้ว';
    case 'rejected': return 'ไม่อนุมัติ';
    default: return status;
  }
};

const LateApproval = ({ user }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [actionLoading, setActionLoading] = useState({});
  const [downloadLoading, setDownloadLoading] = useState({}); 
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getLateRequests();
      setRequests(data);
    } catch (err) {
      setError('ไม่สามารถโหลดข้อมูลคำร้องได้: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAction = async (requestId, action) => {
    const actionText = action === 'approve' ? 'อนุมัติ' : 'ไม่อนุมัติ';
    if (!window.confirm(`คุณต้องการ ${actionText} รายการนี้ใช่หรือไม่?`)) return;

    setActionLoading(prev => ({ ...prev, [requestId]: true }));
    try {
      if (action === 'approve') {
        await api.approveLateRequest(requestId, user.employee_id);
      } else {
        await api.rejectLateRequest(requestId, user.employee_id);
      }
      await fetchData();
    } catch (err) {
      alert(`เกิดข้อผิดพลาด: ` + err.message);
    } finally {
      setActionLoading(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const handleDownloadDocx = (request) => {
    const token = localStorage.getItem('auth_token');
    window.open(`${API_BASE}late-request-download-docx?request_id=${request.id}&token=${token}`, '_blank');
  };

  const currentYear = new Date().getFullYear();
  const allYears = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const filteredRequests = requests.filter(req => {
    if (new Date(req.request_date).getFullYear() !== selectedYear) return false;
    if (filterStatus === 'all') return true;
    return req.status === filterStatus;
  }).sort((a, b) => new Date(b.request_date) - new Date(a.request_date));

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">จัดการคำขออนุญาตมาสาย</h1>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col sm:flex-row justify-between items-center">
          <div className="flex space-x-2">
            {['pending', 'approved', 'rejected', 'all'].map(status => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  filterStatus === status
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status === 'pending' ? 'รอพิจารณา' : status === 'approved' ? 'อนุมัติแล้ว' : status === 'rejected' ? 'ไม่อนุมัติ' : 'ทั้งหมด'}
              </button>
            ))}
          </div>
          <div className="mt-4 sm:mt-0">
             <label className="mr-2 text-sm text-gray-700">เลือกปี:</label>
             <select 
               value={selectedYear} 
               onChange={(e) => setSelectedYear(parseInt(e.target.value))}
               className="border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
             >
               {allYears.map(year => (
                 <option key={year} value={year}>{year + 543}</option>
               ))}
             </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-0 sm:p-6">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <Loader2 className="animate-spin h-10 w-10 text-blue-500" />
            </div>
          ) : error ? (
            <div className="p-4 m-6 bg-red-50 border-l-4 border-red-500 text-red-700 flex items-center">
              <AlertCircle className="mr-2 h-5 w-5 flex-shrink-0" />
              <p>{error}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ผู้ขออนุญาต</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">วันที่ขอ</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">เหตุผล</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">สถานะ</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">จัดการ</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRequests.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center py-8 text-gray-500">
                        ไม่พบข้อมูลคำขอ
                      </td>
                    </tr>
                  ) : (
                    filteredRequests.map(req => (
                      <tr key={req.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{req.first_name} {req.last_name}</div>
                          <div className="text-sm text-gray-500">{req.department}</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {formatDateThai(req.request_date)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700 truncate max-w-xs" title={req.reason}>
                          {req.reason}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(req.status)}`}>
                            {getStatusText(req.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              title="ดาวน์โหลด Word"
                              onClick={() => handleDownloadDocx(req)}
                              disabled={downloadLoading[req.id]}
                              className="p-2 text-blue-600 hover:bg-blue-100 rounded-md disabled:opacity-50 transition-colors"
                            >
                              {downloadLoading[req.id] ? <Loader2 className="h-5 w-5 animate-spin"/> : <Printer className="h-5 w-5" />}
                            </button>
                            
                            {(req.status === 'pending' || req.status === 'rejected') && (
                              <button 
                                  onClick={() => handleAction(req.id, 'approve')} 
                                  disabled={actionLoading[req.id]} 
                                  title={req.status === 'rejected' ? 'เปลี่ยนเป็น อนุมัติ' : 'อนุมัติ'}
                                  className="p-2 text-green-600 hover:bg-green-100 rounded-md disabled:opacity-50 transition-colors"
                              >
                                  {actionLoading[req.id] ? <Loader2 className="h-5 w-5 animate-spin"/> : <CalendarCheck className="h-5 w-5"/>}
                              </button>
                            )}

                            {(req.status === 'pending' || req.status === 'approved') && (
                                <button 
                                  onClick={() => handleAction(req.id, 'reject')} 
                                  disabled={actionLoading[req.id]} 
                                  title={req.status === 'approved' ? 'ยกเลิกการอนุมัติ (เปลี่ยนเป็น ไม่อนุมัติ)' : 'ไม่อนุมัติ'}
                                  className="p-2 text-red-600 hover:bg-red-100 rounded-md disabled:opacity-50 transition-colors"
                                >
                                  {actionLoading[req.id] ? <Loader2 className="h-5 w-5 animate-spin"/> : <CalendarX className="h-5 w-5"/>}
                                </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LateApproval;